import { useEffect } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login/Login';
import Signup from './pages/Signup/Signup';
import UpdatePassword from './pages/Login/UpdatePassword';
import EmailVerify from './pages/Signup/EmailVerify';
import MedicalScribe from './pages/MedicalScribe/MedicalScribe';
import ConsultationSummary from './pages/MedicalScribe/MedicalScribeTranscription.js';
import VitalsRecordView from './pages/MedicalScribe/VitalsRecordView';
import BillingsView from './pages/Billings/BillingsView.js';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import store, { setCurrentTab, setDrawerOpen, resetGlobalState, persistor } from './store.js';
import Onboarding from './pages/Onboarding/Onboarding';
import SettingsPage from './pages/Settings/Settings';
import Chats from './pages/Chats/Chats';
import Appointments from './pages/Appointments/AppointmentView.js';
import Dashboard from './pages/Dashboard/DashboardView.js';
import Summarizer from './pages/Summarizer/Summarizer';
import KnowledgeBase from './pages/KnowledgeBase/KnowledgeBase';
import AgentList from './pages/Agents/AgentList';
import DataExport from './pages/DataExport/DataExport.js';
import UserList from './pages/UserManagement/UserList';
import UserInvite from './pages/Signup/UserInvite';
import SubscriptionTestContainer from './pages/Subscription/SubscriptionTestContainer.js';
import { supabase } from './supabaseClient.js';
import { trackPageView } from './analytics';
import OnboardingVisualTour from './components/OnboardingVisualTour';



const App = () => {
  const { isLoggedIn: loginFlag, userDetails, currentOrg } = useSelector(
    (state) => ({
      isLoggedIn: state.globalState.isLoggedIn,
      userDetails: state.globalState.userDetails,
      currentOrg: state.globalState.currentOrg,
    }),
    shallowEqual
  );
  // A "logged in" UI state without a user identity is a half-state (e.g. an
  // abandoned signup or a purged profile) — treat it as logged out.
  const isLoggedIn = loginFlag && Boolean(userDetails?.id);
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    trackPageView();
  }, [location.pathname]);
  // In-development tabs are enabled per environment through REACT_APP_BETA_TABS,
  // a comma list of: chats, agents, resources (all on in .env.dev, empty on
  // uat/prod). Adjust the env files, never the code, to show/hide one.
  const betaTabs = (process.env.REACT_APP_BETA_TABS || '').split(',').map((s) => s.trim()).filter(Boolean);
  const tabRoutes = {
    '/consultation': 'Patient',
    '/consultationSummary': 'Patient',
    '/vitals': 'Patient',
    '/appointments': 'Appointments',
    '/dashboard': 'Dashboard',
    '/billings': 'Billing',
    ...(betaTabs.includes('chats') ? { '/chats': 'Chats' } : {}),
    ...(betaTabs.includes('agents') ? { '/agents': 'AI Agent' } : {}),
    ...(betaTabs.includes('resources') ? { '/resources': 'Resource Library' } : {}),
    '/users': 'User Management',
    '/export': 'Data Export',
    '/settings': 'Settings',
  };

  const clearStaleState = async (redirect) => {
    dispatch(resetGlobalState());
    await persistor.purge();
    if (redirect && tabRoutes[location.pathname]) {
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        // console.log("No session found, redirecting to login");
        clearStaleState(true);
        return;
      }
      // The persisted UI state must belong to the session's user. A mismatch
      // means another login (or a signup in a second tab of this origin)
      // replaced the token: without this check the UI keeps rendering the old
      // account while every request runs as the new one.
      const persistedUserId = store.getState().globalState.userDetails?.id;
      if (persistedUserId && session.user?.id !== persistedUserId) {
        clearStaleState(true);
      }
    });
    const tab = tabRoutes[location.pathname];
    dispatch(setCurrentTab(tab))
    dispatch(setDrawerOpen(false));
  }, [location]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const persistedUserId = store.getState().globalState.userDetails?.id;
      if (event === 'SIGNED_OUT') {
        if (persistedUserId) {
          dispatch(resetGlobalState());
          persistor.purge();
        }
      } else if (session?.user && persistedUserId && session.user.id !== persistedUserId) {
        dispatch(resetGlobalState());
        persistor.purge();
        window.location.href = '/login';
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
    <Routes>
      <Route path="/" element={<Navigate to="/login" />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/consultation" element={isLoggedIn ? <MedicalScribe /> : <Navigate to="/login" />} />
      <Route path="/consultationSummary" element={isLoggedIn ? <ConsultationSummary /> : <Navigate to="/login" />} />
      <Route path="/vitals" element={isLoggedIn ? <VitalsRecordView /> : <Navigate to="/login" />} />
      <Route path="/appointments" element={isLoggedIn ? <Appointments /> : <Navigate to="/login" />} />
      <Route path="/dashboard" element={isLoggedIn ? <Dashboard /> : <Navigate to="/login" />} />
      {betaTabs.includes('chats') && <Route path="/chats" element={isLoggedIn ? <Chats /> : <Navigate to="/login" />} />}
      {betaTabs.includes('agents') && <Route path="/agents" element={isLoggedIn ? <AgentList /> : <Navigate to="/login" />} />}
      <Route path="/export" element={isLoggedIn ? <DataExport /> : <Navigate to="/login" />} />
      <Route path="/users" element={isLoggedIn ? <UserList /> : <Navigate to="/login" />} />
      <Route path="/billings" element={isLoggedIn ? <BillingsView /> : <Navigate to="/login" />} />
      {/* Legacy route: the wizard replaced CreateProfile for new signups. */}
      <Route path="/createProfile" element={(!isLoggedIn || !currentOrg) ? <Navigate to="/onboarding" /> : <Navigate to="/settings" state={{ openUpdateProfile: true }} />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/settings" element={isLoggedIn ? <SettingsPage /> : <Navigate to="/login" />} />
      <Route path="/emailVerify" element={<EmailVerify />} />
      <Route path="/userInvite" element={<UserInvite />} />
      <Route path="/updatePassword" element={<UpdatePassword />} />
      {betaTabs.includes('resources') && <Route path="/resources" element={isLoggedIn ? <KnowledgeBase /> : <Navigate to="/login" />} />}

      {/* <Route path="/summarizer" element={isLoggedIn ? <Summarizer /> : <Navigate to="/login" />} /> */}
      {/* <Route path="/subscriptionTest" element={<SubscriptionTestContainer />} /> */}
      {/* <Route path="/pdfForm" element={<PDFFormUploader />} /> */}
    </Routes>
    <OnboardingVisualTour userId={userDetails?.id} enabled={isLoggedIn} />
    </>
  );
};

export default App;
